/**
 * <SamMissionCardSkeleton> — la forme de la mission de Sam pendant un chargement.
 *
 * La carte telle qu'elle se rend le plus souvent, une mission à lancer ou à reprendre : le disque
 * de la tête, l'intitulé (il ne dépend d'aucune donnée : il s'écrit), le titre, la barre, la place
 * du bouton, dessous quand la carte est étroite, à droite quand elle est large. Mesurée au même
 * seuil que la carte, et sans aurore : tant que rien n'est arrivé, rien ne dit encore qu'un
 * champion a agi.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { pushButtonBoxHeight } from "../../atoms/Buttons/Button/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { STEP_BAR_HEIGHT } from "../../atoms/SegmentedBar"
import { Skeleton } from "../../atoms/Skeleton"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { Card } from "../Card"
import { SAM_HEAD_SIZE, SAM_MISSION_BESIDE_FROM, SAM_MISSION_PROGRESS_MAX, SAM_MISSION_TEXTS } from "./SamMissionCard"

/** « Je lance la mission » à côté du texte : la largeur du bouton. */
const START_WIDTH = 200

export function SamMissionCardSkeleton() {
    const { onLayout, atLeast: wide } = useWidthAtLeast(SAM_MISSION_BESIDE_FROM)
    const headSize = wide ? SAM_HEAD_SIZE.wide : SAM_HEAD_SIZE.narrow

    return (
        <Card p={wide ? "6" : "4"}>
            <View style={styles.split(wide)} onLayout={onLayout}>
                <Row gap="4" align="center" style={[styles.bow(headSize), wide ? styles.fill : undefined]}>
                    <Skeleton shape="circle" size={headSize} />
                    <Stack gap="3" style={styles.fill}>
                        <Stack gap="1">
                            <SectionLabel>{SAM_MISSION_TEXTS.label}</SectionLabel>
                            <Skeleton textSize={wide ? "text-2xl" : "text-xl"} width={wide ? 320 : "90%"} />
                            {!wide && <Skeleton textSize="text-xl" width="45%" />}
                        </Stack>
                        <Skeleton
                            shape="block"
                            height={STEP_BAR_HEIGHT}
                            width={wide ? SAM_MISSION_PROGRESS_MAX : "100%"}
                        />
                    </Stack>
                </Row>
                <Skeleton shape="block" height={pushButtonBoxHeight("md")} width={wide ? START_WIDTH : "100%"} />
            </View>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    split: (beside: boolean) => ({
        flexDirection: beside ? ("row" as const) : ("column" as const),
        alignItems: beside ? ("center" as const) : ("stretch" as const),
        gap: theme.spacing[beside ? "6" : "4"],
    }),
    fill: { flex: 1, flexShrink: 1, minWidth: 0 },
    bow: (size: number) => ({ paddingBottom: size / 14 }),
}))
